import { Head, Link, router } from "@inertiajs/react"
import { useRef, useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import { objectPositionStyle } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface SubjectRecord {
  id: number
  display_name: string
}

interface SubjectPhoto {
  id: number
  caption: string | null
  display_name: string
  filename: string | null
  width: number | null
  height: number | null
  exclude_from_worksheet: boolean
  focal_point_x?: number | null
  focal_point_y?: number | null
  created_at: string
  image_url: string | null
  thumb_url: string | null
  edit_url: string | null
}

interface Props {
  case_record: CaseRecord
  subject_record: SubjectRecord
  photos: SubjectPhoto[]
}

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

export default function Index({ case_record, subject_record, photos }: Props) {
  const c = case_record
  const s = subject_record

  const [showUpload, setShowUpload] = useState(false)
  const [caption, setCaption] = useState("")
  const [files, setFiles] = useState<FileList | null>(null)
  const [processing, setProcessing] = useState(false)
  const [previews, setPreviews] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    { title: c.display_name || c.case_number, href: `/cases/${c.id}` },
    { title: s.display_name, href: `/cases/${c.id}/subjects/${s.id}` },
    { title: "Photos", href: `/cases/${c.id}/subjects/${s.id}/photos` },
  ]

  const basePath = `/cases/${c.id}/subjects/${s.id}/photos`

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files
    setFiles(selected)

    if (selected) {
      const urls: string[] = []
      for (const file of selected) {
        urls.push(URL.createObjectURL(file))
      }
      setPreviews(urls)
    } else {
      setPreviews([])
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!files || files.length === 0) return

    setProcessing(true)
    const formData = new FormData()
    formData.append("caption", caption)
    for (const file of files) {
      formData.append("files[]", file)
    }

    router.post(basePath, formData, {
      forceFormData: true,
      onFinish: () => {
        setProcessing(false)
        setShowUpload(false)
        setCaption("")
        setFiles(null)
        setPreviews([])
        if (fileRef.current) fileRef.current.value = ""
      },
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Photos - ${s.display_name} - ${c.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {s.display_name} &mdash; {photos.length} Photo
            {photos.length !== 1 ? "s" : ""}
          </h1>
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            {showUpload ? "Cancel" : "Add Photo"}
          </button>
        </div>

        {showUpload && (
          <div className="border-hpi-light-gray mb-6 rounded border p-4">
            <h2 className="mb-3 text-lg font-medium">Upload Photos</h2>
            <form onSubmit={handleSubmit} className="max-w-xl">
              <div className="mb-4">
                <label htmlFor="files" className="mb-1 block font-medium">
                  Select Photos
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  id="files"
                  multiple
                  accept="image/png, image/jpeg"
                  onChange={handleFileChange}
                  required
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                />
                <p className="text-hpi-med-gray mt-1 text-xs">
                  PNG and JPEG files only. Multiple files allowed.
                </p>
              </div>

              {previews.length > 0 && (
                <div className="mb-4 grid grid-cols-4 gap-2">
                  {previews.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt={`Preview ${i + 1}`}
                      className="h-24 w-full rounded border object-cover"
                    />
                  ))}
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="caption" className="mb-1 block font-medium">
                  Caption (optional)
                </label>
                <input
                  type="text"
                  id="caption"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  maxLength={50}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                />
              </div>

              <button
                type="submit"
                disabled={processing || !files || files.length === 0}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
              >
                {processing ? "Uploading..." : "Upload"}
              </button>
            </form>
          </div>
        )}

        {photos.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="border-hpi-light-gray overflow-hidden rounded border"
              >
                <a
                  href={photo.image_url ?? photo.thumb_url ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="bg-hpi-pale-gray flex aspect-square items-center justify-center">
                    {photo.thumb_url ? (
                      <img
                        src={photo.thumb_url}
                        alt={photo.display_name}
                        className="h-full w-full object-cover"
                        style={{ objectPosition: objectPositionStyle(photo) }}
                      />
                    ) : (
                      <span className="text-hpi-med-gray text-sm">
                        No preview
                      </span>
                    )}
                  </div>
                </a>
                <div className="p-2">
                  {photo.caption && (
                    <div className="text-hpi-med-blue truncate text-xs">
                      {photo.caption}
                    </div>
                  )}
                  <div className="text-hpi-med-gray text-xs">
                    {formatDate(photo.created_at)}
                  </div>
                  {photo.width && photo.height && (
                    <div className="text-hpi-med-gray text-xs">
                      {photo.width}x{photo.height}
                    </div>
                  )}
                  {photo.exclude_from_worksheet && (
                    <div className="text-hpi-red mt-1 text-xs">
                      Excluded from worksheet
                    </div>
                  )}
                  {photo.edit_url && (
                    <div className="mt-1">
                      <Link
                        href={photo.edit_url}
                        className="text-hpi-active-blue text-xs hover:underline"
                      >
                        Edit / Crop
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-12 text-center">
            <p className="text-hpi-med-gray mb-4">
              No photos found for this subject.
            </p>
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${c.id}/subjects/${s.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Subject
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
